import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { listPadEntries } from "./api";
import { categoryMeta, formatEntryDetails } from "./padInfoTypes";
import { padFileName } from "./fileActions";

const stamp = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const ACTION_LABEL = {
  created: "Recorded",
  updated: "Edited",
  voided: "Voided",
  verified: "Verified by accounting",
  unverified: "Verification removed",
  attachment_added: "Attached",
  attachment_removed: "Attachment removed",
  archived: "Archived with the tenancy",
};

/**
 * A printable record of everything that has happened to one unit's PAD — every entry and its
 * full history — for accounting's file.
 *
 * Deliberately its own page rather than a print stylesheet over the tenant modal: the modal is
 * a scrolling dialog inside an app shell, and printing that reliably means fighting every
 * ancestor's overflow. A dedicated route renders a document, and the browser prints a document
 * well without help.
 *
 * Opened via /pad-history-print/:itemId. Prints itself once the data lands, so the person who
 * clicked Print gets the dialog without a second click.
 */
export const PadHistoryPrintView = () => {
  const { itemId } = useParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const unitNo = search.get("unit") || "";
  const propertyName = search.get("property") || "";
  const buildingName = search.get("building") || "";
  const autoPrint = search.get("print") !== "0";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // include_archived: the point of a printed history is that nothing is left out.
        const res = await listPadEntries(itemId, true);
        if (!alive) return;
        setEntries(res.data?.data || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.message || "Could not load the PAD history");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [itemId]);

  // The tab title is what print-to-PDF names the file, so it carries the accounting name:
  // "Unit # - 210 The Vibe (PAD History - 2026-08-26)".
  useEffect(() => {
    if (loading || error) return;
    const prev = document.title;
    document.title = padFileName({
      unitNo,
      propertyName,
      categoryLabel: "PAD History",
      date: new Date().toISOString().slice(0, 10),
    });
    return () => { document.title = prev; };
  }, [loading, error, unitNo, propertyName]);

  useEffect(() => {
    if (loading || error || !autoPrint) return;
    // One frame for layout to settle, otherwise the dialog can capture a half-rendered page.
    const t = setTimeout(() => window.print(), 350);
    return () => clearTimeout(t);
  }, [loading, error, autoPrint]);

  const heading = useMemo(
    () => [propertyName, buildingName, unitNo ? `Unit ${unitNo}` : ""].filter(Boolean).join(" · ") || "PAD history",
    [propertyName, buildingName, unitNo]
  );

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spinner /> <span style={{ marginLeft: 8 }}>Loading PAD history…</span>
      </div>
    );
  }
  if (error) return <div style={{ padding: 40, color: "#A32D2D" }}>{error}</div>;

  return (
    <div className="pad-print">
      <style>{`
        .pad-print { max-width: 1000px; margin: 0 auto; padding: 24px 28px 40px; color: #0F1F35;
          font-size: 13px; line-height: 1.45; background: #fff; }
        .pad-print h1 { font-size: 20px; font-weight: 800; margin: 0 0 2px; }
        .pad-print .sub { font-size: 12px; color: #64748B; margin-bottom: 18px; }
        .pad-entry { border: 1px solid #E4E9F2; border-radius: 10px; margin-bottom: 14px; overflow: hidden;
          break-inside: avoid; page-break-inside: avoid; }
        .pad-entry > header { display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px;
          padding: 8px 12px; background: #F7F9FC; border-bottom: 1px solid #E4E9F2; }
        .pad-entry .cat { font-weight: 800; font-size: 13.5px; }
        .pad-entry .id { font-size: 11px; color: #64748B; margin-left: auto; }
        .pad-entry .body { padding: 10px 12px; }
        .pad-entry .details { font-weight: 600; margin-bottom: 2px; }
        .pad-entry .meta { font-size: 11.5px; color: #64748B; }
        .pad-entry .notes { margin-top: 6px; white-space: pre-wrap; }
        .pad-tag { font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 999px;
          background: #EEF0F2; color: #5F5E5A; }
        .pad-hist { margin-top: 10px; border-top: 1px dashed #E4E9F2; padding-top: 8px; }
        .pad-hist h3 { font-size: 11px; text-transform: uppercase; letter-spacing: .07em; color: #64748B;
          font-weight: 700; margin: 0 0 5px; }
        .pad-hist li { list-style: none; margin: 0 0 4px; font-size: 12px; }
        .pad-hist ul { margin: 0; padding: 0; }
        .pad-hist .when { color: #64748B; }
        .pad-hist .chg { color: #334155; padding-left: 14px; }
        .pad-hist .from { text-decoration: line-through; color: #94A3B8; }
        .pad-files { margin-top: 8px; font-size: 12px; color: #334155; }
        .pad-foot { margin-top: 20px; font-size: 11px; color: #94A3B8; }
        @media print {
          @page { size: portrait; margin: 12mm; }
          .pad-print { padding: 0; max-width: none; }
          .pad-entry > header, .pad-tag { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <h1>PAD history — {heading}</h1>
      <div className="sub">
        {entries.length} entr{entries.length === 1 ? "y" : "ies"}, including voided and previous tenancies · printed {stamp(new Date())}
      </div>

      {entries.length === 0 ? <p>No PAD information has been recorded for this unit.</p> : null}

      {entries.map((e) => {
        const meta = categoryMeta(e.category);
        const files = (e.attachments || []).filter((a) => a && !a.removed);
        const audit = [...(e.audit_trail || [])].sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));
        return (
          <section className="pad-entry" key={e._id}>
            <header>
              <span className="cat" style={{ color: meta.color }}>{meta.label}</span>
              <span>effective {e.effective_date || "—"}</span>
              {e.voided ? <span className="pad-tag">Voided</span> : null}
              {e.tenancy_archived ? <span className="pad-tag">Previous resident</span> : null}
              {e.verification?.verified ? <span className="pad-tag">Verified</span> : null}
              <span className="id">{e.pad_id}</span>
            </header>
            <div className="body">
              <div className="details">{formatEntryDetails(e)}</div>
              <div className="meta">
                {e.tenant_names ? `${e.tenant_names} · ` : ""}
                recorded {stamp(e.createdAt)}
                {e.created_by?.name ? ` by ${e.created_by.name}` : ""}
                {e.verification?.verified
                  ? ` · verified by ${e.verification.verified_by_name || "accounting"}${e.verification.verified_at ? ` on ${stamp(e.verification.verified_at)}` : ""}`
                  : ""}
              </div>
              {e.notes ? <div className="notes">{e.notes}</div> : null}
              {e.void_reason ? <div className="notes">Void reason: {e.void_reason}</div> : null}

              {files.length ? (
                <div className="pad-files">
                  <strong>Attachments:</strong> {files.map((f) => f.filename).join(", ")}
                </div>
              ) : null}

              <div className="pad-hist">
                <h3>History</h3>
                <ul>
                  {audit.length === 0 ? <li className="when">Nothing recorded.</li> : null}
                  {audit.map((a, i) => (
                    <li key={i}>
                      <span className="when">{stamp(a.at)}</span> · {ACTION_LABEL[a.action] || a.action}
                      {a.by_name ? ` · ${a.by_name}` : ""}
                      {a.note ? ` — ${a.note}` : ""}
                      {(a.changes || []).map((c, j) => (
                        <div className="chg" key={j}>
                          {c.label}: <span className="from">{c.from}</span> → <strong>{c.to}</strong>
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        );
      })}

      <div className="pad-foot">
        Rentdigicare · PAD entries record what changed and when. Rent comes from the mastersheet; banking details are never stored here.
      </div>
    </div>
  );
};

export default PadHistoryPrintView;
